import React from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import SydLabel from '../../../../../../../commonDesign/SydLabel'
import SydSelect from '../../../../../../../commonDesign/SydSelect'

/**
 * Latch mode dropdown for blueprint presentation editors. Shows the appropriate
 * options based on which latches the selected purpose has configured:
 *   - `selectable` / `latest` when the purpose has a snapshot `latch`.
 *   - `timeseries` when the purpose has a `timeseriesLatch`.
 *
 * Renders nothing when the purpose has neither latch (or hasn't been chosen
 * yet), so the parent editor doesn't need its own guard.
 *
 * Also coerces a stale or invalid saved `value` (e.g. `'selectable'` saved
 * against a purpose that has since dropped its snapshot latch) to the first
 * available option on render — prevents the underlying Select from rendering
 * an out-of-range value or persisting an invalid `latchMode`.
 *
 * Used by `DataGridConfigEditor`, `HierarchicalGridConfigEditor`, and
 * `PresentationTableConfigEditor` — all three previously inlined this logic
 * as an IIFE in their render. Extract makes the duplication go away and gives
 * the picker a DevTools-visible identity.
 */
function LatchModePicker ({ purpose, value, onChange }) {
  const options = [
    ...(purpose?.latch
      ? [
        { label: 'User Selectable', value: 'selectable' },
        { label: 'Latest Only (read-only)', value: 'latest' }
      ]
      : []),
    ...(purpose?.timeseriesLatch
      ? [
        { label: 'Time Series (period + range picker)', value: 'timeseries' }
      ]
      : [])
  ]

  if (options.length === 0) return null

  const effective = options.some(o => o.value === value) ? value : options[0].value

  return (
    <Grid item xs={12}>
      <SydLabel label='Latch Date Mode' helperText='How end users interact with the snapshot date or time-series period'>
        <SydSelect
          options={options}
          value={effective}
          onChange={(e) => onChange(e.target.value)}
        />
      </SydLabel>
    </Grid>
  )
}

LatchModePicker.propTypes = {
  // The purpose object the editor's presentation is bound to. May be null /
  // undefined when no purpose is selected — picker renders nothing in that
  // case. Only the `latch` and `timeseriesLatch` fields are inspected.
  purpose: PropTypes.shape({
    latch: PropTypes.object,
    timeseriesLatch: PropTypes.object
  }),
  // Current saved `latchMode` value. May be stale (e.g. saved before the
  // purpose's latches changed) — the picker coerces internally.
  value: PropTypes.string,
  // Called with the new `latchMode` string when the user picks an option.
  // (The picker unwraps the underlying SydSelect's event for the caller.)
  onChange: PropTypes.func.isRequired
}

export default LatchModePicker
